import { toCanvas, toJpeg, toPng } from 'html-to-image';

import { fixCaptureDomIds, promoteSvgImageHrefs } from './fixCaptureDomIds';
import type { PreviewCaptureFormat, PreviewCaptureResult } from './previewCaptureRegistry';
import { embedPreviewFonts, inlinePublicStorageImages } from './previewCaptureResources';

/** 1×1 transparent GIF. A failed asset stays blank instead of rejecting the capture. */
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const FONTS_READY_CAP_MS = 5_000;

let currentPreviewCapturePhase = 'idle';

export function currentPreviewCapturePhaseForDiagnostics(): string {
  return currentPreviewCapturePhase;
}

export function resetPreviewCapturePhaseForDiagnostics(): void {
  currentPreviewCapturePhase = 'idle';
}

async function waitForPreviewFontsReady(signal?: AbortSignal): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts?.ready) {
    return;
  }
  await Promise.race([
    document.fonts.ready,
    new Promise<void>((resolve) => {
      setTimeout(resolve, FONTS_READY_CAP_MS);
    }),
  ]);
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('Preview capture was aborted.', 'AbortError');
  }
}

function imageSource(target: EventTarget | null): string {
  if (target instanceof HTMLImageElement) {
    return target.currentSrc || target.src;
  }
  if (target instanceof SVGImageElement) {
    return target.href?.baseVal || target.getAttribute('href') || '';
  }
  return '';
}

/**
 * html-to-image rejects with the image's error event when an asset 404s or
 * cannot be fetched. Swap in a transparent pixel so the rest of the frame
 * still rasterizes.
 */
export function keepCaptureGoingAfterImageError(event: Event | string): void {
  if (typeof event === 'string') {
    return;
  }
  const target = event.currentTarget ?? event.target;
  if (target instanceof HTMLImageElement) {
    target.onerror = null;
    target.removeAttribute('srcset');
    target.src = TRANSPARENT_PIXEL;
    return;
  }
  if (target instanceof SVGImageElement) {
    target.onerror = null;
    target.setAttribute('href', TRANSPARENT_PIXEL);
  }
}

export function describeCaptureFailure(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof Event !== 'undefined' && error instanceof Event) {
    const src = imageSource(error.target).replace(/[?#].*$/, '');
    return src
      ? `Preview image failed to load: ${src}`
      : `Preview capture failed (${error.type} event).`;
  }
  return 'Preview capture failed.';
}

/** A same-origin SVG copied here becomes a bitmap at its intrinsic size and scales up soft. */
function isSvgImageSource(src: string): boolean {
  return /\.svg([?#]|$)/i.test(src);
}

/** Same-origin rasters can be copied without a second fetch. Cross-origin ones taint the canvas. */
function inlineDecodedImages(root: ParentNode): void {
  root.querySelectorAll('img').forEach((img) => {
    if (
      !img.complete ||
      img.naturalWidth === 0 ||
      img.src.startsWith('data:') ||
      isSvgImageSource(img.src)
    ) {
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      context.drawImage(img, 0, 0);
      img.src = canvas.toDataURL('image/png');
    } catch {
      // Leave the URL for html-to-image to fetch.
    }
  });
}

type PreparedPreviewRaster = {
  width: number;
  height: number;
  warnings: string[];
  rasterOptions: Parameters<typeof toPng>[1] & object;
  restore: () => void;
};

function previewFrameSize(element: HTMLElement): { width: number; height: number } {
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  if (width <= 0 || height <= 0) {
    throw new Error('The preview frame has no visible size.');
  }
  return { width, height };
}

function captureBackgroundTarget(element: HTMLElement, backgroundColor?: string): HTMLElement {
  if (!backgroundColor) {
    return element;
  }
  const scaled = element.querySelector('[data-testid="preview-scaled-content"]');
  return scaled instanceof HTMLElement ? scaled : element;
}

/** Embed fonts and inline images once. Call `restore` when the capture or sequence ends. */
async function preparePreviewRaster(
  element: HTMLElement,
  signal?: AbortSignal,
): Promise<PreparedPreviewRaster> {
  const { width, height } = previewFrameSize(element);

  currentPreviewCapturePhase = 'fonts.ready';
  await waitForPreviewFontsReady(signal);

  currentPreviewCapturePhase = 'prepareDom';
  promoteSvgImageHrefs(element);
  fixCaptureDomIds(element);
  inlineDecodedImages(element);
  currentPreviewCapturePhase = 'embedPreviewFonts';
  const { css: fontEmbedCSS, warnings: fontWarnings } = await embedPreviewFonts(element, signal);
  currentPreviewCapturePhase = 'inlinePublicStorageImages';
  const { restore, warnings: imageWarnings } = await inlinePublicStorageImages(element, signal);
  currentPreviewCapturePhase = 'rasterize';

  const rasterOptions = {
    // Supplying this (including an empty string) bypasses html-to-image's global CSS scan.
    fontEmbedCSS,
    // A timestamp query misses the URL the <img> already loaded, including signed
    // CDN urls and `?width=` resizes. Keep that query string as the cache key.
    includeQueryParams: true,
    imagePlaceholder: TRANSPARENT_PIXEL,
    onImageErrorHandler: keepCaptureGoingAfterImageError,
    // html-to-image defaults this to devicePixelRatio. width/height are CSS
    // pixels, so a 2x display would return a bitmap that does not match them.
    pixelRatio: 1,
    fetchRequestInit: signal ? { signal } : undefined,
    // The capture host sits off-screen. Force the clone visible so inherited
    // visibility does not rasterize as an empty JPEG/PNG.
    style: { visibility: 'visible', opacity: '1' },
  };
  return {
    width,
    height,
    warnings: [...fontWarnings, ...imageWarnings],
    rasterOptions,
    restore,
  };
}

export type PreviewRasterSession = {
  width: number;
  height: number;
  warnings: string[];
  /** Rasterize the element as it is painted now. */
  rasterizeToCanvas: () => Promise<HTMLCanvasElement>;
  close: () => void;
};

/** One asset preparation for many frames of the same preview element. */
export async function openPreviewRasterSession(
  element: HTMLElement,
  signal?: AbortSignal,
): Promise<PreviewRasterSession> {
  const prepared = await preparePreviewRaster(element, signal);
  let closed = false;
  return {
    width: prepared.width,
    height: prepared.height,
    warnings: prepared.warnings,
    rasterizeToCanvas: async () => {
      if (closed) {
        throw new Error('The preview raster session is closed.');
      }
      promoteSvgImageHrefs(element);
      fixCaptureDomIds(element);
      return toCanvas(element, {
        ...prepared.rasterOptions,
        canvasWidth: prepared.width,
        canvasHeight: prepared.height,
      });
    },
    close: () => {
      if (!closed) {
        closed = true;
        prepared.restore();
        resetPreviewCapturePhaseForDiagnostics();
      }
    },
  };
}

export async function rasterizePreviewElement(
  element: HTMLElement,
  options: {
    format: PreviewCaptureFormat;
    quality?: number;
    signal?: AbortSignal;
    backgroundColor?: string;
  },
): Promise<PreviewCaptureResult> {
  const { width, height, warnings, rasterOptions, restore } = await preparePreviewRaster(
    element,
    options.signal,
  );
  const captureRoot = captureBackgroundTarget(element, options.backgroundColor);
  const previousBackground = captureRoot.style.backgroundColor;
  if (options.backgroundColor) {
    captureRoot.style.backgroundColor = options.backgroundColor;
  }
  let dataUrl: string;
  try {
    dataUrl =
      options.format === 'png'
        ? await toPng(element, rasterOptions)
        : await toJpeg(element, { ...rasterOptions, quality: options.quality ?? 0.85 });
  } finally {
    if (options.backgroundColor) {
      captureRoot.style.backgroundColor = previousBackground;
    }
    // Keep later captures able to re-read an asset replaced at the same public URL.
    restore();
    resetPreviewCapturePhaseForDiagnostics();
  }

  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  if (!base64) {
    throw new Error('Preview capture returned an empty image.');
  }

  return {
    base64,
    mimeType: options.format === 'png' ? 'image/png' : 'image/jpeg',
    width,
    height,
    ...(warnings.length > 0 ? { warnings } : {}),
  };
}
